import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useGetSupportTicketCounts } from '@/api/control/control-plane/control-plane';
import { PageHeader } from '@/components/common/PageHeader';
import { HelpCenterCard } from '@/components/support/HelpCenterCard';
import { PartnerCard } from '@/components/support/PartnerCard';
import { SupportTicketsTable } from '@/components/support/SupportTicketsTable';
import { SupportWaitingBanner } from '@/components/support/SupportWaitingBanner';
import { VisibilityCard } from '@/components/support/VisibilityCard';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import type { SupportTicketTab } from '@/lib/support';

/**
 * Support home: the organization's tickets (Open by default), a banner when support is waiting on
 * a reply, and the help center, partner and visibility cards. Available to every organization member.
 */
export default function SupportPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tab, setTab] = useState<SupportTicketTab>('open');
  const { data: countsResponse } = useGetSupportTicketCounts(orgId ?? '', { query: { enabled: !!orgId } });
  const counts = countsResponse?.data;

  if (!orgId) {
    return null;
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t.support.title} description={t.support.description}>
        <Button onClick={() => { void navigate(`/dashboard/orgs/${orgId}/support/tickets/new`); }}>
          <Plus className="mr-2 h-4 w-4" />
          {t.support.tickets.newTicket}
        </Button>
      </PageHeader>
      <SupportWaitingBanner count={counts?.needsReply ?? 0} onShow={() => { setTab('needsReply'); }} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <SupportTicketsTable orgId={orgId} tab={tab} onTabChange={setTab} counts={counts} />
        <div className="space-y-6">
          <HelpCenterCard />
          <PartnerCard orgId={orgId} />
          <VisibilityCard orgId={orgId} />
        </div>
      </div>
    </div>
  );
}
